import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './EventList.css';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';

const EventList = ({ orgId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);

  const [formData, setFormData] = useState({
    eventName: '',
    eventDescription: '',
    eventType: 'ACADEMIC',
    eventStartDate: '',
    eventEndDate: '',
    subEventName: '',
    subEventDate: '',
    subEventDuration: '',
    subEventLocation: '',
  });

  const { axios, token, userRole } = useAuth();
  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'SUPERADMIN' || userRole === 'ROLE_ADMIN';
  const navigate = useNavigate();

  // Map backend event types to user-friendly display names
  const displayTypeMap = {
    ACADEMIC: 'Educational',
    EDUCATIONAL: 'Educational',
    HOLIDAY: 'Holidays',
    HOLIDAYS: 'Holidays',
    EXAM_TERM: 'Exams',
    EXAM: 'Exams',
    MEETING: 'Meetings',
    MEETINGS: 'Meetings',
  };

  const fetchEvents = useCallback(async () => {
    console.log("🔑 [AUTH TOKEN] Events Page Loaded / Fetching with Token:", token);
    if (!orgId || !token) {
      setLoading(false);
      setError(!orgId ? 'Organization ID missing' : 'Authentication token missing');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fixed start date: Only events from 2025-10-29 onwards
      const startDate = new Date('2025-10-29T00:00:00Z');
      const endDate = new Date(startDate);
      endDate.setFullYear(endDate.getFullYear() + 2); // Up to Oct 2027 to cover future events

      const formatISO = (date) => date.toISOString().slice(0, 19) + 'Z';

      const eventsUrl = (configData.RESOURCES_EVENTS || '').split('?')[0];

      const response = await axios.get(eventsUrl, {
        params: {
          orgId,
          eventStartDate: formatISO(startDate),
          eventEndDate: formatISO(endDate),
          pageSize: 100,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      const items = response.data?.items || [];

      const formattedEvents = items.flatMap((event, index) => {
        const backendType = (event.eventType || '').toUpperCase();
        const eventType = displayTypeMap[backendType] || 'General';
        const realEventId = event.eventId || event.id || event._id || `event-${index}`;

        const scheduleMap = event.eventScheduleMap || {};
        let subEvents = [];

        // Safely extract all sub-events from any key (e.g., "Day 1", "main")
        Object.values(scheduleMap).forEach(arr => {
          if (Array.isArray(arr)) {
            subEvents.push(...arr);
          }
        });

        // Filter valid sub-events
        const validSubEvents = subEvents.filter(se =>
          se &&
          typeof se === 'object' &&
          se.subEventName &&
          se.subEventName.trim() &&
          se.subEventName.toLowerCase() !== 'string' &&
          se.eventDateAndTime
        );

        if (validSubEvents.length === 0) {
          // Fallback: use main event if no valid sub-events
          if (!event.eventName || !event.eventStartDate) return [];

          return [{
            eventId: realEventId || `fallback-${index}`,
            realEventId,
            eventName: event.eventName.trim(),
            eventType,
            rawEventType: backendType || 'ACADEMIC',
            eventDescription: event.eventDescription || 'No description available',
            eventStartDate: event.eventStartDate || '',
            eventEndDate: event.eventEndDate || '',
            subEventName: event.eventName.trim(),
            subEventDateAndTime: event.eventStartDate || '',
            subEventDuration: '',
            subEventLocation: '',
            eventDate: new Date(event.eventStartDate).toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }),
            location: 'N/A',
            duration: 'N/A',
            rawDate: new Date(event.eventStartDate),
          }];
        }

        // Map valid sub-events
        return validSubEvents.map((sub, subIndex) => {
          const dateObj = new Date(sub.eventDateAndTime);
          return {
            eventId: `${realEventId}-sub${subIndex}`,
            realEventId,
            eventName: sub.subEventName.trim(),
            eventType,
            rawEventType: backendType || 'ACADEMIC',
            eventDescription: event.eventDescription || 'No description',
            eventStartDate: event.eventStartDate || '',
            eventEndDate: event.eventEndDate || '',
            subEventName: sub.subEventName.trim(),
            subEventDateAndTime: sub.eventDateAndTime,
            subEventDuration: sub.durationMins || '',
            subEventLocation: sub.location || '',
            eventDate: dateObj.toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }),
            location: sub.location || 'N/A',
            duration: sub.durationMins ? `${sub.durationMins} mins` : 'N/A',
            rawDate: dateObj, // For sorting
          };
        });
      });

      // Sort by most recent first
      formattedEvents.sort((a, b) => b.rawDate - a.rawDate);

      setEvents(formattedEvents);
      setLoading(false);
    } catch (err) {
      console.error('Fetch events error:', err);
      const msg = err.response?.data?.message ||
        err.response?.status === 401 ? 'Session expired. Please log in again.' :
        err.response?.status === 403 ? 'Access denied.' :
          'Failed to load events. Please try again.';

      setError(msg);
      setLoading(false);
    }
  }, [axios, orgId, token]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleEventClick = useCallback((event) => {
    console.log("🔑 [AUTH TOKEN] Clicked Event Token:", token);
    console.log("📌 Selected Event Details:", event);
    navigate('/events/details', { state: { event } });
  }, [navigate, token]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditEvent = (event, e) => {
    if (e) e.stopPropagation();

    if (!isAdmin) {
      toast.error('Only admin accounts are allowed to edit events.');
      return;
    }

    setEditingEventId(event.realEventId);

    const formatForInput = (isoStr) => {
      if (!isoStr) return '';
      try {
        const d = new Date(isoStr);
        if (isNaN(d.getTime())) return '';
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      } catch (err) {
        return '';
      }
    };

    setFormData({
      eventName: event.eventName || '',
      eventDescription: event.eventDescription !== 'No description' && event.eventDescription !== 'No description available' ? event.eventDescription || '' : '',
      eventType: event.rawEventType || 'ACADEMIC',
      eventStartDate: formatForInput(event.eventStartDate || event.subEventDateAndTime),
      eventEndDate: formatForInput(event.eventEndDate || event.subEventDateAndTime),
      subEventName: event.subEventName || '',
      subEventDate: formatForInput(event.subEventDateAndTime || event.eventStartDate),
      subEventDuration: event.subEventDuration ? String(event.subEventDuration) : '',
      subEventLocation: event.location !== 'N/A' ? event.location || '' : '',
    });

    setShowForm(true);
  };

  const handleDeleteEvent = async (event, e) => {
    if (e) e.stopPropagation();

    if (!isAdmin) {
      toast.error('Only admin accounts are allowed to delete events.');
      return;
    }

    const confirmDelete = window.confirm(`Are you sure you want to delete "${event.eventName}"?`);
    if (!confirmDelete) return;

    try {
      const eventsUrl = (configData.RESOURCES_EVENTS || '').split('?')[0];
      await axios.delete(
        `${eventsUrl}/${event.realEventId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      toast.success(`"${event.eventName}" deleted successfully!`);
      fetchEvents();
    } catch (err) {
      console.error('Delete event error:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to delete event');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!isAdmin) {
      toast.error('Only admin accounts are allowed to manage events.');
      return;
    }

    if (!formData.eventName.trim()) {
      toast.error('Event Name is required.');
      return;
    }

    try {
      const now = new Date();
      const start = formData.eventStartDate ? new Date(formData.eventStartDate) : now;
      const end = formData.eventEndDate ? new Date(formData.eventEndDate) : new Date(start.getTime() + 86400000);
      const subDate = formData.subEventDate ? new Date(formData.subEventDate) : start;

      if (isNaN(start.getTime()) || isNaN(end.getTime()) || isNaN(subDate.getTime())) {
        throw new Error('Invalid date selected');
      }

      if (end < start) throw new Error('End date cannot be before start date');

      const subName = formData.subEventName.trim() || formData.eventName.trim();

      const payload = {
        academicYear: '2025-2026',
        eventName: formData.eventName.trim(),
        eventDescription: formData.eventDescription.trim() || null,
        eventType: formData.eventType || 'ACADEMIC',
        eventStartDate: start.toISOString(),
        eventEndDate: end.toISOString(),
        orgId,
        eventScheduleMap: {
          "Day 1": [
            {
              subEventName: subName,
              eventDateAndTime: subDate.toISOString(),
              durationMins: parseInt(formData.subEventDuration) || 60,
              location: formData.subEventLocation.trim() || 'Not specified',
            }
          ]
        },
      };

      const eventsUrl = (configData.RESOURCES_EVENTS || '').split('?')[0];

      if (editingEventId) {
        await axios.put(
          `${eventsUrl}/${editingEventId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        toast.success('Event updated successfully!');
      } else {
        await axios.post(
          eventsUrl,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        toast.success('Event created successfully!');
      }

      setShowForm(false);
      setEditingEventId(null);
      setFormData({
        eventName: '',
        eventDescription: '',
        eventType: 'ACADEMIC',
        eventStartDate: '',
        eventEndDate: '',
        subEventName: '',
        subEventDate: '',
        subEventDuration: '',
        subEventLocation: '',
      });
      fetchEvents(); // Refresh list
    } catch (err) {
      console.error('Submit event error:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to save event');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingEventId(null);
    setFormData({
      eventName: '',
      eventDescription: '',
      eventType: 'ACADEMIC',
      eventStartDate: '',
      eventEndDate: '',
      subEventName: '',
      subEventDate: '',
      subEventDuration: '',
      subEventLocation: '',
    });
  };

  return (
    <div className="event-list-container">


      <div className="event-list-header">
        <h3 onClick={() => console.log("Auth Token:", token)} style={{ cursor: 'pointer' }}>Events</h3>
        {isAdmin && (
          <button className="add-event-button" onClick={() => { setEditingEventId(null); setShowForm(true); }}>
            +
          </button>
        )}
      </div>

      {/* Create / Edit Event Form */}
      {showForm && (
        <div className="event-form-overlay">
          <div className="event-form-container">
            <h4>{editingEventId ? 'Edit Event' : 'Create New Event'}</h4>
            <form onSubmit={handleFormSubmit} className="event-form" noValidate>
              <label>Event Name * <input type="text" name="eventName" value={formData.eventName} onChange={handleFormChange} /></label>
              <label>Description <textarea name="eventDescription" value={formData.eventDescription} onChange={handleFormChange} /></label>

              <label>Event Type
                <select name="eventType" value={formData.eventType} onChange={handleFormChange}>
                  <option value="ACADEMIC">Educational</option>
                  <option value="HOLIDAY">Holiday</option>
                  <option value="EXAM_TERM">Exam Term</option>
                  <option value="MEETING">Meeting</option>
                </select>
              </label>

              <label>Start Date <input type="datetime-local" name="eventStartDate" value={formData.eventStartDate} onChange={handleFormChange} /></label>
              <label>End Date <input type="datetime-local" name="eventEndDate" value={formData.eventEndDate} onChange={handleFormChange} /></label>

              <label>Sub-Event Name <input type="text" name="subEventName" value={formData.subEventName} onChange={handleFormChange} /></label>
              <label>Sub-Event Date & Time <input type="datetime-local" name="subEventDate" value={formData.subEventDate} onChange={handleFormChange} /></label>
              <label>Duration (mins) <input type="number" name="subEventDuration" value={formData.subEventDuration} onChange={handleFormChange} min="1" /></label>
              <label>Location <input type="text" name="subEventLocation" value={formData.subEventLocation} onChange={handleFormChange} /></label>

              <div className="event-form-buttons">
                <button type="submit">{editingEventId ? 'Update Event' : 'Create Event'}</button>
                <button type="button" onClick={handleFormClose}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Event List */}
      <div className="event-list">
        {loading ? (
          <p className="status-message">Loading events...</p>
        ) : error ? (
          <div className="status-message error">
            <strong>Error:</strong> {error}
            <br />
            <button onClick={fetchEvents} style={{ marginTop: '15px', padding: '10px 20px' }}>
              Retry
            </button>
          </div>
        ) : events.length === 0 ? (
          <p className="status-message">No events found after October 29, 2025.</p>
        ) : (
          events.map((event) => (
            <div
              key={event.eventId}
              className="event-item"
              onClick={() => handleEventClick(event)}
            >
              <div className="event-name">{event.eventName}</div>
              <div className="event-type">{event.eventType}</div>
              <div className="event-date">{event.eventDate}</div>
              <div className="event-location">Location: {event.location} • Duration: {event.duration}</div>
              <div className="event-description">{event.eventDescription}</div>
              {isAdmin && (
                <div className="event-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="event-action-btn edit" onClick={(e) => handleEditEvent(event, e)} title="Edit Event">
                    ✏️
                  </button>
                  <button className="event-action-btn delete" onClick={(e) => handleDeleteEvent(event, e)} title="Delete Event">
                    🗑️
                  </button>
                </div>
              )}
              <div className="event-separator"></div>
            </div>
          ))
        )}
      </div>

    </div>
  );
};

export default React.memo(EventList);